// ============================================================================
// api_remoteDevices.ts
// CRUD devices distants + envoi d'invitation par email
// ============================================================================

import { RemoteDevice, DeviceAuthMode } from '../../dataTypes/liveStreamManagment/types_webrtc';
import { API_URLS }                     from '../urls';

export const fetchRemoteDevices = async (
  serverUrl: string,
  streamPath: string
): Promise<RemoteDevice[]> => {
  const res = await fetch(`${serverUrl}/${streamPath}/webrtc/remoteDevices?include_passwords=true`);
  const data = await res.json();
  return data.devices ?? [];
};

export interface RegisteredRemoteDevice {
  device_id    : string;
  label        : string;
  password     : string | null;
  auth_mode    : DeviceAuthMode;
  access_token : string | null;
}

export const registerRemoteDevice = async (
  serverUrl: string,
  streamPath: string,
  device: { device_id: string; label: string; password?: string; auth_mode?: DeviceAuthMode; access_token?: string }
): Promise<RegisteredRemoteDevice> => {
  const body: Record<string, string> = { label: device.label };
  // Mode "uuid" : l'identifiant technique est dérivé du nom d'affichage côté serveur.
  if (device.device_id.trim())        body.device_id    = device.device_id.trim();
  if (device.password?.trim())        body.password     = device.password.trim();
  if (device.auth_mode)               body.auth_mode    = device.auth_mode;
  // Token déjà communiqué au porteur : le serveur doit reprendre celui-ci.
  if (device.access_token?.trim())    body.access_token = device.access_token.trim();
  const res = await fetch(`${serverUrl}/${streamPath}/webrtc/remoteDevices/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail ?? `HTTP ${res.status}`);
  return data;
};

/**
 * Mode "uuid" : vérifie auprès du microservice de la chaîne que le token de
 * 5 caractères issu du lien donne bien droit à une connexion.
 * Renvoie null si le lien est inconnu ou a été révoqué.
 */
export const resolveDeviceAccessToken = async (
  serverUrl  : string,
  channelUuid: string,
  accessToken: string,
): Promise<{ device_id: string; label: string; is_connected: boolean } | null> => {
  // La page device ne connaît que l'hôte (« cast.kleonpro.tv ») là où la régie
  // manipule une URL complète : on accepte les deux formes.
  const base = /^https?:\/\//i.test(serverUrl) ? serverUrl : `https://${serverUrl}`;
  const res = await fetch(
    // ⚠️ `channelUuid` porte en réalité l'ADRESSE PUBLIQUE de la chaîne : elle
    // vient du lien d'invitation, qui l'embarque depuis la fermeture de la voie
    // live. Le nom du paramètre ment ; la valeur, elle, est la bonne — et c'est
    // la seule qui route encore.
    `${base}/${channelUuid}/webrtc/remoteDevices/resolve/${encodeURIComponent(accessToken)}`,
    { cache: 'no-store' },
  );
  if (res.status === 404) return null;
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail ?? `HTTP ${res.status}`);
  }
  return res.json();
};

/**
 * Le token tiré par la régie est-il libre sur cette chaîne ?
 * S'appuie sur la résolution : 404 = aucun device ne le porte.
 */
export const isDeviceAccessTokenFree = async (
  serverUrl  : string,
  channelUuid: string,
  accessToken: string,
): Promise<boolean> => (await resolveDeviceAccessToken(serverUrl, channelUuid, accessToken)) === null;

export const regenerateDeviceAccessToken = async (
  serverUrl : string,
  streamPath: string,
  device_id : string
): Promise<{ new_access_token: string }> => {
  const res = await fetch(
    `${serverUrl}/${streamPath}/webrtc/remoteDevices/${device_id}/regenerate-token`,
    { method: 'POST' }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail ?? `HTTP ${res.status}`);
  return data;
};

export const deleteRemoteDevice = async (
  serverUrl: string,
  streamPath: string,
  device_id: string
): Promise<void> => {
  const res = await fetch(`${serverUrl}/${streamPath}/webrtc/remoteDevices/${device_id}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
};

export const regenerateDevicePassword = async (
  serverUrl: string,
  streamPath: string,
  device_id: string
): Promise<{ new_password: string }> => {
  const res = await fetch(
    `${serverUrl}/${streamPath}/webrtc/remoteDevices/${device_id}/regenerate-password`,
    { method: 'POST' }
  );
  const data = await res.json();
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return data;
};

export const updateRemoteDevice = async (
  serverUrl : string,
  streamPath: string,
  device_id : string,
  patch     : { new_device_id?: string; password?: string; label?: string; is_director?: boolean }
): Promise<void> => {
  // Champs omis = champs non touchés côté microservice. C'est ce qui permet de
  // renommer un device sans lui retirer son rôle Réalisateur au passage.
  const body: Record<string, string | boolean> = {};
  if (patch.new_device_id !== undefined) body.new_device_id = patch.new_device_id;
  if (patch.password      !== undefined) body.password      = patch.password;
  if (patch.label         !== undefined) body.label         = patch.label;
  if (patch.is_director   !== undefined) body.is_director   = patch.is_director;

  const res = await fetch(
    `${serverUrl}/${streamPath}/webrtc/remoteDevices/${device_id}`,
    {
      method  : 'PATCH',
      headers : { 'Content-Type': 'application/json' },
      body    : JSON.stringify(body),
    }
  );
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail ?? `HTTP ${res.status}`);
  }
};

export interface SendDeviceInvitationEmailParams {
  serverUrl      : string;
  streamPath     : string;
  deviceId       : string;
  password       : string;
  webRtcPageUrl  : string;
  recipientEmail : string;
  channelName    : string;
  /** 'uuid' → template réduit (nom de la chaîne + lien), sans identifiant ni mot de passe. */
  authMode       : DeviceAuthMode;
  /** Nom donné au téléphone dans la régie — repris par le mail (« Votre alias »). */
  alias?         : string;
}

export const sendDeviceInvitationEmail = async (
  params: SendDeviceInvitationEmailParams
): Promise<void> => {
  const res = await fetch(API_URLS.postRemoteDeviceInformationsNotification, {
    method  : 'POST',
    headers : { 'Content-Type': 'application/json' },
    body    : JSON.stringify({
      recipient_email : params.recipientEmail,
      stream_path     : params.streamPath,
      device_id       : params.deviceId,
      password        : params.password,
      webrtc_page_url : params.webRtcPageUrl,
      channel_name    : params.channelName,
      auth_mode       : params.authMode,
      alias           : params.alias ?? '',
      channel         : 'email',
    }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail ?? data.message ?? `HTTP ${res.status}`);
  }
};

export interface SendDeviceInvitationWhatsAppParams extends Omit<SendDeviceInvitationEmailParams, 'recipientEmail'> {
  /** Identifiant WhatsApp du destinataire : un numéro de téléphone. */
  recipientWhatsapp : string;
}

/**
 * Mêmes informations de connexion, second canal — c'est le même endpoint, seul
 * `channel` change.
 *
 * Différence de fond avec le mail : WhatsApp répond. Le serveur renvoie 502 quand
 * Meta refuse le message (numéro inconnu, fenêtre de 24 h fermée, modèle non
 * approuvé, configuration absente) et le motif remonte ici — c'est cette phrase
 * que la régie affiche à l'opérateur, destinataire par destinataire.
 */
export const sendDeviceInvitationWhatsApp = async (
  params: SendDeviceInvitationWhatsAppParams
): Promise<void> => {
  const res = await fetch(API_URLS.postRemoteDeviceInformationsNotification, {
    method  : 'POST',
    headers : { 'Content-Type': 'application/json' },
    body    : JSON.stringify({
      recipient_whatsapp : params.recipientWhatsapp,
      stream_path        : params.streamPath,
      device_id          : params.deviceId,
      password           : params.password,
      webrtc_page_url    : params.webRtcPageUrl,
      channel_name       : params.channelName,
      auth_mode          : params.authMode,
      alias              : params.alias ?? '',
      channel            : 'whatsapp',
    }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail ?? data.message ?? `HTTP ${res.status}`);
  }
};


// ── Caméra RTMP ─────────────────────────────────────────────────────────────
//
// Endpoint distinct de celui des caméras Smartphone/Web, parce que les
// informations n'ont rien de commun : là un lien de connexion, ici un serveur et
// une clé à reporter dans un encodeur. Le canal, lui, se dit de la même façon.

export interface SendRtmpCameraInvitationParams {
  channel          : 'email' | 'whatsapp';
  /** Adresse ou numéro, selon le canal. */
  recipient        : string;
  channelName      : string;
  cameraLabel      : string;
  rtmpServer       : string;
  rtmpKey          : string;
  rtmpIngestUrl    : string;
}

export const sendRtmpCameraInvitation = async (
  params: SendRtmpCameraInvitationParams
): Promise<void> => {
  const res = await fetch(API_URLS.postRtmpCameraInformationsNotification, {
    method  : 'POST',
    headers : { 'Content-Type': 'application/json' },
    body    : JSON.stringify({
      channel            : params.channel,
      recipient_email    : params.channel === 'email'    ? params.recipient : '',
      recipient_whatsapp : params.channel === 'whatsapp' ? params.recipient : '',
      channel_name       : params.channelName,
      camera_label       : params.cameraLabel,
      rtmp_server        : params.rtmpServer,
      rtmp_key           : params.rtmpKey,
      rtmp_ingest_url    : params.rtmpIngestUrl,
    }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail ?? data.message ?? `HTTP ${res.status}`);
  }
};


export interface SendWebTvInvitationEmailParams {
  recipientEmail  : string;
  channelName     : string;
  webTvUrl        : string;
}

export const sendWebTvInvitationEmail = async (
  params: SendWebTvInvitationEmailParams
): Promise<void> => {

  console.log ('sendWebTvInvitationEmail', params)	

  const res = await fetch(API_URLS.postWebTvInvitationNotification, {
    method  : 'POST',
    headers : { 'Content-Type': 'application/json' },
    body    : JSON.stringify({
      recipient_email : params.recipientEmail,
      channel_name    : params.channelName,
      webtv_url       : params.webTvUrl,
    }),
  });
  
  console.log ('sendWebTvInvitationEmail', res)	
  
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail ?? data.message ?? `HTTP ${res.status}`);
  }
};

export interface SendWebTvInvitationEmailToManyParams {
  recipientEmails : string[];
  channelName     : string;
  webTvUrl        : string;
}

export const sendWebTvInvitationEmailToMany = async (
  params: SendWebTvInvitationEmailToManyParams
): Promise<void> => {

  console.log ('sendWebTvInvitationEmailToMany', params)

  await sendWebTvInvitationEmail({
    recipientEmail : params.recipientEmails.join(';'),
    channelName    : params.channelName,
    webTvUrl       : params.webTvUrl,
  });
};

export interface SendWebTvInvitationWhatsAppParams {
  recipientWhatsapp : string;
  channelName       : string;
  webTvUrl          : string;
}

/**
 * Adresse de la chaîne, second canal — même endpoint que
 * sendWebTvInvitationEmail, seul `channel` change. Un destinataire par appel,
 * comme sendDeviceInvitationWhatsApp : Meta répond, l'échec doit remonter
 * destinataire par destinataire (cf. WebTvInformationsNotificationCreation).
 */
export const sendWebTvInvitationWhatsApp = async (
  params: SendWebTvInvitationWhatsAppParams
): Promise<void> => {
  const res = await fetch(API_URLS.postWebTvInvitationNotification, {
    method  : 'POST',
    headers : { 'Content-Type': 'application/json' },
    body    : JSON.stringify({
      recipient_whatsapp : params.recipientWhatsapp,
      channel_name       : params.channelName,
      webtv_url          : params.webTvUrl,
      channel            : 'whatsapp',
    }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail ?? data.message ?? `HTTP ${res.status}`);
  }
};